(() => {
  if (window.__sqMenuSearchHoverBridgeV1) return;
  window.__sqMenuSearchHoverBridgeV1 = true;

  const MAX_CARDS = 99;
  const SEARCH_VIEW = 'sq-menu';
  const SEARCH_PAGE_CACHE = new Map();
  const SEARCH_META_CACHE = new Map();
  const COLLECTION_CACHE = new Map();
  const PREVIEW_TOKENS = new WeakMap();
  const BOUND_ANCHORS = new WeakSet();
  const DRAG_READY = new WeakSet();

  const MIXED_COLLECTIONS = {
    '/collections/after-dark-edit': {
      searches: ['after dark fashion', 'night out outfit'],
    },
    '/collections/the-vacation-edit': {
      searches: ['vacation outfit', 'resort wear'],
      collections: ['/collections/coastal-swimwear', '/collections/coastal-cover-ups'],
    },
    '/collections/event-outfit-panic': {
      searches: ['event dress', 'formal party dress'],
    },
    '/collections/the-statement-edit': {
      searches: ['statement fashion', 'bold outfit'],
    },
    '/collections/the-everyday-edit': {
      searches: ['everyday wardrobe', 'casual chic'],
    },
    '/collections/desk-to-dinner': {
      searches: ['work dress', 'evening outfit'],
      collections: ['/collections/polished-blazers'],
    },
    '/collections/statement-outerwear': {
      searches: ['statement outerwear'],
      collections: ['/collections/signature-jackets', '/collections/statement-coats'],
    },
    '/collections/statement-coats': {
      searches: ['cape outerwear'],
    },
    '/collections/signature-dresses': {
      searches: ['trending dress'],
      collections: ['/collections/maxi-dresses', '/collections/midi-dresses', '/collections/mini-dresses'],
    },
  };

  const SEARCH_FALLBACKS = {
    'queen life': ['/collections/queen-life-trending'],
    seasonal: ['/collections/velvet-mistletoe', '/collections/the-heart-affair', '/collections/champagne-at-midnight'],
    'special occasion': ['/collections/event-outfit-panic', '/collections/signature-dresses'],
    'personal style': ['/collections/most-loved-styles'],
    'intimate lingerie': ['/collections/modern-bodysuits', '/collections/sculpt-shapewear', '/collections/soft-loungewear'],
    edgy: ['/collections/the-statement-edit'],
    'polka dot': ['/collections/prints'],
    scarf: ['/collections/polished-accessories'],
    'hair accessory': ['/collections/polished-accessories'],
    'travel bag': ['/collections/carry-the-moment'],
  };

  function secureRandom(max) {
    if (max <= 1) return 0;
    if (window.crypto?.getRandomValues) {
      const value = new Uint32Array(1);
      window.crypto.getRandomValues(value);
      return value[0] % max;
    }
    return Math.floor(Math.random() * max);
  }

  function shuffle(list) {
    const result = list.slice();
    for (let index = result.length - 1; index > 0; index -= 1) {
      const swapIndex = secureRandom(index + 1);
      [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
    }
    return result;
  }

  function normalizeQuery(value) {
    return String(value || '').replace(/\+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function submenuFor(anchor) {
    return anchor?.closest?.('.menu-list__submenu') ||
      anchor?.closest?.('.menu-list__list-item')?.querySelector?.(':scope > .menu-list__submenu') ||
      null;
  }

  function previewHost(submenu) {
    return submenu?.querySelector?.('.mega-menu__grid') ||
      submenu?.querySelector?.('[data-menu-grid-id]') ||
      submenu?.querySelector?.('.mega-menu') ||
      null;
  }

  function ensurePreview(submenu) {
    const host = previewHost(submenu);
    if (!host) return null;

    let preview = host.querySelector(':scope > [data-sq-menu-product-preview]');
    if (preview) return preview;

    preview = document.createElement('div');
    preview.className = 'sq-menu-product-preview';
    preview.dataset.sqMenuProductPreview = '';

    const list = host.querySelector(':scope > .mega-menu__list');
    if (list) list.insertAdjacentElement('afterend', preview);
    else host.appendChild(preview);
    return preview;
  }

  function ensurePreviewStructure(preview) {
    let title = preview.querySelector('[data-sq-menu-preview-title]');
    let hint = preview.querySelector('.sq-menu-product-preview__hint');
    let viewport = preview.querySelector('[data-sq-menu-preview-viewport]');
    let track = preview.querySelector('[data-sq-menu-preview-track]');

    if (!title || !hint || !viewport || !track) {
      preview.innerHTML = `
        <div class="sq-menu-product-preview__heading">
          <span data-sq-menu-preview-title></span>
          <span class="sq-menu-product-preview__hint"></span>
        </div>
        <div class="sq-menu-product-preview__viewport" data-sq-menu-preview-viewport>
          <div class="sq-menu-product-preview__track" data-sq-menu-preview-track></div>
        </div>`;
      title = preview.querySelector('[data-sq-menu-preview-title]');
      hint = preview.querySelector('.sq-menu-product-preview__hint');
      viewport = preview.querySelector('[data-sq-menu-preview-viewport]');
      track = preview.querySelector('[data-sq-menu-preview-track]');
    }

    enableBasicDrag(viewport);
    return { title, hint, viewport, track };
  }

  function enableBasicDrag(viewport) {
    if (!viewport || DRAG_READY.has(viewport)) return;
    DRAG_READY.add(viewport);

    let active = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;
    let pointerId = null;

    viewport.tabIndex = viewport.tabIndex >= 0 ? viewport.tabIndex : 0;
    viewport.setAttribute('aria-label', 'Scroll menu products left or right');
    viewport.addEventListener('dragstart', (event) => event.preventDefault());

    viewport.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.pointerType !== 'mouse' || event.button !== 0) return;
      active = true;
      moved = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = viewport.scrollLeft;
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!active || event.pointerId !== pointerId) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) < 5 && !moved) return;
      moved = true;
      viewport.classList.add('is-dragging');
      try { viewport.setPointerCapture(pointerId); } catch (_) {}
      viewport.scrollLeft = startScroll - delta;
      event.preventDefault();
    });

    const stop = (event) => {
      if (!active || event.pointerId !== pointerId) return;
      active = false;
      viewport.classList.remove('is-dragging');
      try {
        if (viewport.hasPointerCapture(pointerId)) viewport.releasePointerCapture(pointerId);
      } catch (_) {}
      pointerId = null;
    };

    viewport.addEventListener('pointerup', stop);
    viewport.addEventListener('pointercancel', stop);
    viewport.addEventListener('click', (event) => {
      if (!moved) return;
      moved = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    viewport.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      viewport.scrollBy({ left: direction * Math.max(220, viewport.clientWidth * 0.75), behavior: 'smooth' });
      event.preventDefault();
    });
  }

  function showLoading(preview, label) {
    const ui = ensurePreviewStructure(preview);
    ui.title.textContent = label || 'Collection preview';
    ui.hint.textContent = 'Loading products';
    preview.classList.add('is-loading');
  }

  function productImage(product) {
    const first = Array.isArray(product?.images) ? product.images[0] : null;
    if (typeof first === 'string') return first;
    return first?.src || product?.image?.src || product?.featured_image || '';
  }

  function optimizedImageUrl(source, width = 560) {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      if (/shopify|cdn\.shopify/i.test(url.hostname)) url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return source;
    }
  }

  function buildCollectionCard(product) {
    const imageSource = optimizedImageUrl(productImage(product));
    if (!product?.handle || !imageSource) return null;

    const card = document.createElement('a');
    card.className = 'sq-menu-product-card';
    card.href = `/products/${encodeURIComponent(product.handle)}`;
    card.dataset.sqMenuProductCard = '';
    card.dataset.productKey = String(product.id || product.handle);
    card.setAttribute('aria-label', product.title || 'View product');

    const media = document.createElement('span');
    media.className = 'sq-menu-product-card__media';
    const image = document.createElement('img');
    image.src = imageSource;
    image.alt = product.title || '';
    image.width = 560;
    image.height = 700;
    image.loading = 'lazy';
    image.decoding = 'async';
    media.appendChild(image);

    const title = document.createElement('span');
    title.className = 'sq-menu-product-card__title';
    title.textContent = product.title || '';
    card.append(media, title);
    return card;
  }

  async function fetchCollectionCards(pathname) {
    const path = pathname.replace(/\/$/, '');
    if (COLLECTION_CACHE.has(path)) return COLLECTION_CACHE.get(path).map((card) => card.cloneNode(true));

    const request = (async () => {
      const url = new URL(`${path}/products.json`, window.location.origin);
      url.searchParams.set('limit', String(MAX_CARDS));
      url.searchParams.set('page', '1');
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
      const data = await response.json();
      const cards = [];
      for (const product of Array.isArray(data?.products) ? data.products : []) {
        const card = buildCollectionCard(product);
        if (card) cards.push(card);
      }
      return cards;
    })();

    COLLECTION_CACHE.set(path, await request);
    return COLLECTION_CACHE.get(path).map((card) => card.cloneNode(true));
  }

  function parseSearchPage(html) {
    const documentObject = new DOMParser().parseFromString(html, 'text/html');
    const root = documentObject.querySelector('[data-sq-menu-search-results]');
    if (!root) return { cards: [], pages: 1, total: 0 };
    return {
      cards: Array.from(root.querySelectorAll('.sq-menu-product-card')).map((card) => card.cloneNode(true)),
      pages: Math.max(1, Number(root.dataset.pages || 1)),
      total: Math.max(0, Number(root.dataset.total || 0)),
    };
  }

  async function fetchSearchPage(query, page) {
    const normalized = normalizeQuery(query);
    const cacheKey = `${normalized}|${page}`;
    if (SEARCH_PAGE_CACHE.has(cacheKey)) {
      return SEARCH_PAGE_CACHE.get(cacheKey).map((card) => card.cloneNode(true));
    }

    const url = new URL('/search', window.location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', normalized);
    url.searchParams.set('view', SEARCH_VIEW);
    url.searchParams.set('page', String(page));

    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
    const parsed = parseSearchPage(await response.text());
    SEARCH_META_CACHE.set(normalized, { pages: parsed.pages, total: parsed.total });
    SEARCH_PAGE_CACHE.set(cacheKey, parsed.cards);
    return parsed.cards.map((card) => card.cloneNode(true));
  }

  async function fetchRandomSearchCards(query) {
    const normalized = normalizeQuery(query);
    const meta = SEARCH_META_CACHE.get(normalized);
    if (!meta) return fetchSearchPage(normalized, 1);
    const page = secureRandom(Math.max(1, meta.pages)) + 1;
    return fetchSearchPage(normalized, page);
  }

  function sourceFor(anchor) {
    if (!(anchor instanceof HTMLAnchorElement)) return null;
    let url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (_) {
      return null;
    }
    if (url.origin !== window.location.origin) return null;

    const mixed = MIXED_COLLECTIONS[url.pathname];
    if (mixed) {
      return {
        key: `mixed:${url.pathname}`,
        label: anchor.textContent.trim(),
        collections: [url.pathname, ...(mixed.collections || [])],
        searches: mixed.searches || [],
      };
    }

    if (url.pathname.replace(/\/$/, '') === '/search') {
      const query = normalizeQuery(url.searchParams.get('q'));
      if (!query) return null;
      return {
        key: `search:${query}`,
        label: anchor.textContent.trim(),
        collections: SEARCH_FALLBACKS[query] || [],
        searches: [query],
      };
    }

    return null;
  }

  function dedupeCards(cards) {
    const unique = [];
    const seen = new Set();
    for (const card of cards) {
      if (!(card instanceof Element)) continue;
      const href = card.getAttribute('href') || '';
      const key = card.dataset.productKey || href.replace(/[?#].*$/, '');
      if (!key || seen.has(key)) continue;
      seen.add(key);
      unique.push(card);
    }
    return unique;
  }

  async function loadSource(source) {
    const requests = [];
    for (const path of source.collections) requests.push(fetchCollectionCards(path));
    for (const query of source.searches) requests.push(fetchRandomSearchCards(query));
    const groups = await Promise.allSettled(requests);
    const cards = [];
    for (const group of groups) {
      if (group.status === 'fulfilled') cards.push(...group.value);
    }
    return shuffle(dedupeCards(cards)).slice(0, MAX_CARDS);
  }

  function renderCards(preview, label, cards) {
    const ui = ensurePreviewStructure(preview);
    const fragment = document.createDocumentFragment();
    cards.forEach((card, index) => {
      const image = card.querySelector('img');
      if (image) {
        image.loading = index < 4 ? 'eager' : 'lazy';
        if (index < 3) image.fetchPriority = 'high';
      }
      fragment.appendChild(card);
    });

    ui.title.textContent = label || 'Collection preview';
    ui.hint.textContent = cards.length ? `Swipe to browse ${cards.length} unique products` : 'No matching products found';
    ui.track.replaceChildren(fragment);
    ui.viewport.scrollLeft = 0;
    preview.classList.remove('is-loading', 'is-switching');
    preview.dataset.sqCustomSource = label || '';
    window.dispatchEvent(new Event('resize'));
  }

  async function showCustom(anchor, source) {
    const submenu = submenuFor(anchor);
    if (!submenu) return;
    const preview = ensurePreview(submenu);
    if (!preview) return;

    const token = (PREVIEW_TOKENS.get(preview) || 0) + 1;
    PREVIEW_TOKENS.set(preview, token);
    showLoading(preview, source.label);

    try {
      const cards = await loadSource(source);
      if (PREVIEW_TOKENS.get(preview) !== token) return;
      renderCards(preview, source.label, cards);
    } catch (error) {
      console.warn('[Serein Queen search hover]', error);
      if (PREVIEW_TOKENS.get(preview) !== token) return;
      renderCards(preview, source.label, []);
    }
  }

  function forceNativeReshuffle(anchor) {
    let url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (_) {
      return;
    }
    if (url.origin !== window.location.origin || !url.pathname.includes('/collections/')) return;
    if (MIXED_COLLECTIONS[url.pathname]) return;
    const preview = ensurePreview(submenuFor(anchor));
    if (!preview) return;
    preview.dataset.collectionUrl = '';
    preview.dataset.pendingCollectionUrl = '';
  }

  function bindAnchor(anchor) {
    if (!(anchor instanceof HTMLAnchorElement) || BOUND_ANCHORS.has(anchor)) return;
    BOUND_ANCHORS.add(anchor);

    const onPointerEnter = (event) => {
      const source = sourceFor(anchor);
      if (!source) {
        forceNativeReshuffle(anchor);
        return;
      }
      event.stopImmediatePropagation();
      showCustom(anchor, source);
    };

    const onFocus = (event) => {
      const source = sourceFor(anchor);
      if (!source) {
        forceNativeReshuffle(anchor);
        return;
      }
      event.stopImmediatePropagation();
      showCustom(anchor, source);
    };

    anchor.addEventListener('pointerenter', onPointerEnter, true);
    anchor.addEventListener('focus', onFocus, true);
  }

  function bind(root = document) {
    root.querySelectorAll?.('#header-component .mega-menu__link[href]').forEach(bindAnchor);
    if (root.matches?.('#header-component .mega-menu__link[href]')) bindAnchor(root);
  }

  function start() {
    bind();
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          bind(node);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

